import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { Label, Popover } from 'flowbite-react'

import CustomTextInput from '@/components/basic/input-text/input-text.component'
import CustomSelect from '@/components/basic/select/select.component'
import { useRevisarPedidosContext } from '@/contexts/revisar-pedido.context'
import { getStoredValue } from '@/hooks/common/use-session-storage'

const RevisarPedidoForm = () => {
  const {
    formik,
    handlePodeAprovar,
    contratos,
    handlePrevisaoDisponibilidadeChange,
    getPagMinDate
  } = useRevisarPedidosContext()

  const allowAdditionalOrderTypes = getStoredValue('allowAdditionalOrderTypes') === 'true'

  const tipoPedidoVendaOptions = allowAdditionalOrderTypes
    ? [
        { label: 'Mensal', value: 'Mensal' },
        { label: 'Complementar', value: 'Completo' },
        { label: 'Admitidos', value: 'Admitidos' }
      ]
    : [
        { label: 'Mensal', value: 'Mensal' },
        { label: 'Admitidos', value: 'Admitidos' }
      ]

  function getMinDisponibilidadeDate() {
    if (
      contratos?.conteudo?.find(c => c.idUnidade === null)?.modalidade ===
      'Prepago'
    ) {
      const previsaoPagamento = formik.getFieldProps('previsaoPagamento').value
      if (previsaoPagamento != undefined) {
        const pagamentoDate = new Date(previsaoPagamento)
        pagamentoDate.setDate(pagamentoDate.getDate() + 7)
        return pagamentoDate.toISOString().split('T')[0]
      }
    }
    const todayDate = new Date()
    todayDate.setDate(todayDate.getDate() + 7)
    return todayDate.toISOString().split('T')[0]
  }

  function handlePrevisaoPagamento(novaPrevisao: string) {
    const novaPrevisaoDate = new Date(novaPrevisao)
    formik.setFieldValue(
      'previsaoPagamento',
      novaPrevisaoDate.toISOString().split('T')[0]
    )
    if (
      contratos?.conteudo?.find(c => c.idUnidade === null)?.modalidade ===
      'Prepago'
    ) {
      const disponibilidadeDate = new Date(
        formik.getFieldProps('previsaoDisponibilidade').value
      )
      novaPrevisaoDate.setDate(novaPrevisaoDate.getDate() + 7)

      if (novaPrevisaoDate > disponibilidadeDate) {
        formik.setFieldValue(
          'previsaoDisponibilidade',
          novaPrevisaoDate.toISOString().split('T')[0]
        )
      }
    }
    handlePodeAprovar(false)
  }

  return (
    <form
      onSubmit={formik.handleSubmit}
      className='flex flex-col md:grid grid-cols-12 w-full p-3 bg-white gap-5 mb-5 rounded-lg'
    >
      <div className='flex flex-col col-span-4 justify-left items-left gap-2'>
        <div>
          <Label>Data de Início Utilização</Label>
          <div className='gap-2'>
            <CustomTextInput
              type='date'
              field='periodoUtilizacaoInicial'
              data-testid='periodoUtilizacaoInicial'
              formik={formik}
              max={formik.values.periodoUtilizacaoFinal}
              value={formik.values.periodoUtilizacaoInicial}
              label=''
              required
            />
          </div>
        </div>
        <div>
          <div className='flex items-center gap-2 pb-1'>
            <Label>Data Previsão para Pagamento</Label>
            <Popover
              trigger='hover'
              content={
                <div className='w-64 text-xs text-gray-800 dark:text-gray-400'>
                  <div className='px-3 py-2'>
                    <p>
                      Ao alterar a data previsão para pagamento o pedido terá de
                      ser reprocessado.
                    </p>
                  </div>
                </div>
              }
              placement='top'
            >
              <InformationCircleIcon className='h-4 w-4 text-primary' />
            </Popover>
          </div>

          <div className='gap-2'>
            <CustomTextInput
              type='date'
              field='previsaoPagamento'
              data-testid='previsaoPagamento'
              formik={formik}
              min={getPagMinDate()}
              value={formik?.values.previsaoPagamento}
              onChange={value => handlePrevisaoPagamento(value.target.value)}
              label=''
              required
            />
          </div>
        </div>
      </div>
      <div className='flex flex-col col-span-4 justify-right items-right gap-2'>
        <div>
          <Label>Data de Fim Utilização</Label>
          <div className='gap-2'>
            <CustomTextInput
              type='date'
              field='periodoUtilizacaoFinal'
              data-testid='periodoUtilizacaoFinal'
              formik={formik}
              min={formik.values.periodoUtilizacaoInicial}
              value={formik?.values.periodoUtilizacaoFinal}
              label=''
              required
            />
          </div>
        </div>
        <div>
          <div className='flex items-center gap-2 pb-1'>
            <Label>Data Previsão Disponibilização</Label>
            <Popover
              trigger='hover'
              content={
                <div className='w-64 text-xs text-gray-800 dark:text-gray-400'>
                  <div className='px-3 py-2'>
                    <p>
                      Ao alterar a data de disponibilização o pedido terá de ser
                      reprocessado.
                    </p>
                  </div>
                </div>
              }
              placement='top'
            >
              <InformationCircleIcon className='h-4 w-4 text-primary' />
            </Popover>
          </div>
          <div className='gap-2'>
            <CustomTextInput
              type='date'
              field='previsaoDisponibilidade'
              data-testid='previsaoDisponibilidade'
              formik={formik}
              min={getMinDisponibilidadeDate()}
              value={formik?.values.previsaoDisponibilidade}
              label=''
              className='w-full'
              required
              onChange={e =>
                handlePrevisaoDisponibilidadeChange(e.target.value)
              }
            />
          </div>
        </div>
      </div>
      <div className='col-span-4'>
        <CustomSelect
          options={tipoPedidoVendaOptions}
          field='tipoPedidoVenda'
          data-testid='tipoPedidoVenda'
          formik={formik}
          value={formik.values.tipoPedidoVenda}
          onChange={value => {
            handlePodeAprovar(false)
            formik.setFieldValue('tipoPedidoVenda', value.target.value)
          }}
          label='Tipo de Pedido de Venda'
        />
      </div>
    </form>
  )
}

export default RevisarPedidoForm
